import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../hooks/useApi.js';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // Set once a temp-password login comes back — swaps the form below into
  // "choose your own password" mode instead of a normal email/password login.
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [newPassword, setNewPassword]   = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const navigate = useNavigate();
  // Where to go after logging in: the page a link pointed at, if it's one of ours.
  const nextPath = (() => {
    const n = new URLSearchParams(window.location.search).get('next') || '';
    return n.startsWith('/') && !n.startsWith('//') && !n.startsWith('/login') ? n : '/';
  })();

  // Invite link from the "Your Blake account" email: /login?invite=…
  const inviteToken = new URLSearchParams(window.location.search).get('invite');
  const [invitee, setInvitee] = useState(null);   // { name, email } once checked
  const [inviteError, setInviteError] = useState('');
  useEffect(() => {
    if (!inviteToken) return;
    apiFetch('/auth/invite/check', { method: 'POST', body: { token: inviteToken }, skipAuth: true })
      .then(r => setInvitee(r.data ?? r))
      .catch(e => setInviteError(e.message || 'This invite link has expired or already been used.'));
  }, [inviteToken]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch('/auth/login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      });
      if (res.requiresPasswordChange) {
        setPreAuthToken(res.preAuthToken);
        return;
      }
      localStorage.setItem('pld_token', res.accessToken || res.data?.token || res.token);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSetPassword(e) {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords don’t match'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/auth/complete-password-change', {
        method: 'POST',
        body: { preAuthToken, newPassword },
        skipAuth: true,
      });
      localStorage.setItem('pld_token', res.accessToken || res.data?.token || res.token);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err.message || 'Could not set new password');
    } finally {
      setLoading(false);
    }
  }

  async function handleAcceptInvite(e) {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('Your password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords don’t match'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/auth/accept-invite', { method: 'POST', body: { token: inviteToken, newPassword }, skipAuth: true });
      localStorage.setItem('pld_token', res.accessToken || res.data?.accessToken || res.token);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err.message || 'Could not set your password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-mark">B</div>
          <div>
            <div className="login-logo-name">Blake</div>
            <div className="login-logo-sub">Pleasant Land Distillery</div>
          </div>
        </div>

        {invitee && !preAuthToken ? (
          <form onSubmit={handleAcceptInvite} className="login-form">
            <p style={{ margin: '0 0 4px', fontSize: 14 }}>
              Welcome{invitee.name ? `, ${invitee.name.split(' ')[0]}` : ''}! Choose a password for <strong>{invitee.email}</strong>.
            </p>
            <div className="login-field">
              <label className="login-label">Password (8+ characters)</label>
              <input className="login-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} minLength={8} required autoFocus autoComplete="new-password" />
            </div>
            <div className="login-field">
              <label className="login-label">Confirm password</label>
              <input className="login-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} minLength={8} required autoComplete="new-password" />
            </div>
            {error && <p className="login-error">{error}</p>}
            <button className="login-btn" disabled={loading}>
              {loading ? 'Setting password…' : 'Set password and sign in'}
            </button>
          </form>
        ) : preAuthToken ? (
          <form onSubmit={handleSetPassword} className="login-form">
            <p style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--text-muted)' }}>
              Choose your own password to finish signing in.
            </p>
            <div className="login-field">
              <label className="login-label">New password</label>
              <input
                className="login-input"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                minLength={8}
                required
                autoFocus
              />
            </div>
            <div className="login-field">
              <label className="login-label">Confirm new password</label>
              <input
                className="login-input"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            {error && <p className="login-error">{error}</p>}
            <button className="login-btn" disabled={loading}>
              {loading ? 'Setting password…' : 'Set password and sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            {inviteError && <p className="login-error" style={{ marginTop: 0 }}>{inviteError}</p>}
            <div className="login-field">
              <label className="login-label">Email</label>
              <input
                className="login-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="login-field">
              <label className="login-label">Password</label>
              <input
                className="login-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="login-error">{error}</p>}
            <button className="login-btn" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          background: var(--pld-green);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        .login-card {
          background: var(--surface);
          border-radius: var(--radius-lg);
          padding: 40px;
          width: 100%;
          max-width: 380px;
          box-shadow: var(--shadow-lg);
        }
        .login-logo {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 32px;
        }
        .login-logo-mark {
          width: 44px; height: 44px;
          background: var(--pld-green);
          color: var(--pld-yellow);
          font-weight: 800;
          font-size: 16px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .login-logo-name {
          font-size: 16px;
          font-weight: 700;
          color: var(--text);
        }
        .login-logo-sub {
          font-size: 12px;
          color: var(--text-muted);
        }
        .login-form { display: flex; flex-direction: column; gap: 16px; }
        .login-field { display: flex; flex-direction: column; gap: 6px; }
        .login-label { font-size: 13px; font-weight: 500; color: var(--text); }
        .login-input {
          padding: 9px 12px;
          border-radius: var(--radius);
          border: 1px solid var(--border);
          font-size: 14px;
          background: var(--surface-2);
          transition: border-color var(--transition);
        }
        .login-input:focus { border-color: var(--pld-green); background: var(--surface); outline: none; }
        .login-error {
          color: var(--status-critical);
          font-size: 13px;
          padding: 8px 12px;
          background: #fef2f2;
          border-radius: var(--radius-sm);
        }
        .login-btn {
          background: var(--pld-green);
          color: #fff;
          padding: 10px;
          border-radius: var(--radius);
          font-size: 14px;
          font-weight: 600;
          margin-top: 4px;
          transition: background var(--transition);
        }
        .login-btn:hover:not(:disabled) { background: var(--pld-green-dark); }
        .login-btn:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
